<template>
  <div>
    <template v-for="(block, index) in renderedBlocks" :key="index">
      <template v-if="block.segments">
        <template v-for="(segment, segmentIndex) in block.segments" :key="segmentIndex">
          <ul v-if="segment.type === 'list'" class="mb-2.5 list-disc pl-5 [&_li_p]:mb-0" data-testid="wiki-text-list">
            <li v-for="(item, itemIndex) in segment.items" :key="itemIndex">
              <WikiTextBlock v-bind="sharedBlockProps" :text="item.wikiText" :markdown-only="item.markdownOnly && !plaintext" />
            </li>
          </ul>
          <WikiTextBlock v-else v-bind="sharedBlockProps" :text="segment.wikiText" :markdown-only="segment.markdownOnly && !plaintext" />
        </template>
      </template>
      <WikiTextBlock
        v-else
        v-bind="sharedBlockProps"
        :text="block.wikiText"
        :markdown-only="block.markdownOnly && !plaintext"
        :locked="block.locked"
        :locked-full="block.lockedFull"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { breakdownWikiTextBlocks, segmentVisualLists } from '@/util/tokenize_wiki_text'
import WikiTextBlock from './WikiTextBlock.vue'
import type { Resource } from '@/resources/resource_types'
import type { PopoverSide, WikiTextLockMode, WikiTextStrategy } from './wiki_text_types'

const SNIPPET_FIRST_BLOCK_MODES: WikiTextLockMode[] = ['completely_blur', 'always_snippet_first_block']

const props = withDefaults(defineProps<{
  text?: string
  strategies?: Record<string, WikiTextStrategy>
  resources?: Record<string, Resource>
  plaintext?: boolean
  popoverPlacement?: PopoverSide
  locked?: boolean
  lockMode?: WikiTextLockMode
}>(), {
  text: '',
  strategies: () => ({}),
  resources: () => ({}),
  popoverPlacement: 'right',
  lockMode: 'default',
})

const wikiTextBlocks = computed(() => breakdownWikiTextBlocks(props.text))
const hasMultipleBlocks = computed(() => wikiTextBlocks.value.length > 1)

// A segmented list is never locked itself, but it can still be in lock_links mode, so every
// block gets lockedLinks — otherwise a link inside a rebuilt list would become clickable.
const sharedBlockProps = computed(() => ({
  plaintext: props.plaintext,
  popoverPlacement: props.popoverPlacement,
  strategies: props.strategies,
  resources: props.resources,
  lockedLinks: props.locked && props.lockMode === 'lock_links',
}))

const isBlockLocked = (index: number) => {
  if (!props.locked) return false
  if (hasMultipleBlocks.value && index === 0) return SNIPPET_FIRST_BLOCK_MODES.includes(props.lockMode)
  return props.lockMode !== 'lock_links'
}

const isSingleMarkdownImage = (block: { wikiText: string, markdownOnly: boolean }) =>
  block.markdownOnly && !!block.wikiText && block.wikiText.split(' ').length === 1

// Fully locked means no visible teaser: every block after the first, a block that is only an
// image, or everything in completely_blur mode.
const isBlockFullyLocked = (block: { wikiText: string, markdownOnly: boolean }, index: number) =>
  props.locked && (
    (hasMultipleBlocks.value && index > 0) ||
    isSingleMarkdownImage(block) ||
    props.lockMode === 'completely_blur'
  )

// Locked blocks show a blurred teaser built from the whole block, so only unlocked blocks
// are split into real lists.
const renderedBlocks = computed(() => wikiTextBlocks.value.map((block, index) => {
  const locked = isBlockLocked(index)
  const lockedFull = isBlockFullyLocked(block, index)
  return {
    ...block,
    locked,
    lockedFull,
    segments: locked ? null : segmentVisualLists(block.wikiText),
  }
}))
</script>
